import {
  IStoriesBlock,
  IStoriesBlockCard,
  IStoriesBlockPost,
  IStory,
  IStoryCard,
} from '../../../types/stories'
import { mediaCdnReplace } from '../../../utils/mediaCdnReplace'

const text = (value: unknown) => (typeof value === 'string' ? value.trim() : '')
/** a number or a string id, as text; "" for null or anything else */
const idText = (value: unknown) =>
  typeof value === 'number' && Number.isFinite(value)
    ? String(value)
    : text(value)
/** a positive whole number, sent as a number or as text */
const count = (value: unknown): number | '' => {
  const n = Number(idText(value))
  return idText(value) !== '' && Number.isInteger(n) && n > 0 ? n : ''
}

const toCard = (card: IStoriesBlockCard, index: number): IStoryCard | null => {
  const video = card?.video ?? card
  const kalturaId = text(video?.kalturaId)
  if (!kalturaId) {
    return null
  }
  const order = Number(card.storyCardOrder)
  return {
    order: Number.isFinite(order) && order > 0 ? order : index + 1,
    id: video?.id === undefined || video?.id === null ? '' : String(video.id),
    kalturaId,
    name: text(video?.name),
    duration: Number(video?.duration) || 0,
    onAirID: text(video?.onAirID),
    description: text(video?.description),
  }
}

const toStory = (post: IStoriesBlockPost): IStory | null => {
  const id = post?.id === undefined || post?.id === null ? '' : String(post.id)
  const cover = post?.storyCoverImage
  // the CMS's images go through the site's own host swap, as everywhere else
  const coverD = mediaCdnReplace(text(cover?.d) || text(cover?.m))
  const cards = (Array.isArray(post?.storyCards) ? post.storyCards : [])
    .map(toCard)
    .filter((card): card is IStoryCard => card !== null)
    .sort((a, b) => a.order - b.order)
  if (!id || !coverD || cards.length === 0) {
    return null
  }
  return {
    id,
    link: text(post.link),
    title: text(post.title),
    boldTitle: text(post.storyBoldTitle),
    storyTitle: text(post.storyTitle),
    episodeNumber: count(post.episodeNumber),
    seriesId: idText(post.seriesId),
    // sent exactly as the CMS wrote it
    seriesName: typeof post.seriesName === 'string' ? post.seriesName : '',
    cover: {
      d: coverD,
      m: mediaCdnReplace(text(cover?.m)) || coverD,
      alt: text(cover?.alt),
    },
    cards,
  }
}

/**
 * The valid stories of a block, in the order the CMS sent them (newest first).
 * A story needs an id, a cover and one card with a Kaltura id; anything else in
 * the data is optional and never throws.
 */
export const parseStories = (block: IStoriesBlock | null | undefined) => {
  const posts = Array.isArray(block?.posts) ? block?.posts ?? [] : []
  const seen = new Set<string>()
  return posts.map(toStory).filter((story): story is IStory => {
    if (!story || seen.has(story.id)) {
      return false
    }
    seen.add(story.id)
    return true
  })
}

/** "פרק 1: יאיר מובא למנוחות" - the bold part as sent, then the title */
export const storyLabel = (story: Pick<IStory, 'boldTitle' | 'storyTitle'>) =>
  [story.boldTitle, story.storyTitle].filter(Boolean).join(' ')

/** the line under a clip: its own text, or the story's label */
export const cardCaption = (story: IStory, card: IStoryCard) =>
  card.description || storyLabel(story)
